"use client"
import { useState, useRef } from "react"
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  IconButton,
  useTheme,
  Paper,
  Tabs,
  Tab,
  Fade,
  Button,
  alpha,
  Tooltip,
  useMediaQuery,
} from "@mui/material"
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos"
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import StarIcon from "@mui/icons-material/Star"
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome"
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch"
import SecurityIcon from "@mui/icons-material/Security"
import SettingsIcon from "@mui/icons-material/Settings"
import MapIcon from "@mui/icons-material/Map"
import DiamondIcon from "@mui/icons-material/Diamond"
import DiscordIcon from "@mui/icons-material/Chat"
import FullscreenIcon from "@mui/icons-material/Fullscreen"
import CloseIcon from "@mui/icons-material/Close"
import MemoryIcon from "@mui/icons-material/Memory"
import WifiTetheringIcon from "@mui/icons-material/WifiTethering"
import { FaSteam } from "react-icons/fa"
import config from "../config"
import ImageFanDisplay from "../components/ImageFanDisplay"
import { Link as RouterLink } from "react-router-dom"

// Feature categories with icons and alphabetically sorted features
const featureCategories = [
  {
    name: "Core Features",
    icon: <StarIcon />,
    features: ["Box Collector", "NPC Killer /w Box Collector", "Adv. Jump cpu to travel", "Hangar switch /w config load", "Ship Follow / Train (NPC)"].sort(),
  },
  {
    name: "Special Zones",
    icon: <MapIcon />,
    features: ["Cubi Graveyard traveller", "Frozen Labyrinth", "LoW", "Hitac Map Farm", "Cubikon Farm X-6", "Mimesis Mutiny", "Treacherous Domain"].sort(),
  },
  {
    name: "Galaxy Gates",
    icon: <RocketLaunchIcon />,
    features: [
	  "Deep Space Echoes",
      "All Galaxy Gates (inc. Hades)",
      "Astral",
      "Eternal Blacklight Gate (EBG)",
      "Gauntlet Of Plutus (GoP)",
      "Quarantine Zone (QZ)"
    ].sort(),
  },
  {
    name: "Resource Collection",
    icon: <DiamondIcon />,
    features: [
	  "Auto Galaxy Trek",
      "Auto Bonus calendar",
      "Auto buy items",
      "Auto Daily login calendar",
      "Dispatch Retrievers",
      "Dispatch Gates",
      "Ore Trader (for Palladium)"
    ].sort(),
  },
  {
    name: "Tools & Modifications",
    icon: <SettingsIcon />,
    features: [
      "Anti push",
      "Auto-cloaker",
      "Profile Switcher for all events (e.g.: GG discount, Agatus or GoP)",
      "Proxifier Support",
      "Skylab",
	  "Mail - auto-claim reward mails",
      "DarkShop (hidden shop)",
      "Time Table"
    ].sort(),
  },
  {
    name: "PLUS",
    icon: <AutoAwesomeIcon />,
    accent: "#ffca28",
    features: ["Map Cycle (for Behemoth, Stroke, or Saturns)", "Novas", "Assembly - auto craft & collect",
      "Tool - autolock, cloak hotkey",
	  "Auction auto bid",
      "Ship Killer"
    ].sort(),
    specialRequirement: {
      text: "Requires additional PLUS license",
      highlight: true,
    },
  },
  {
    name: "Base Operations",
    icon: <SecurityIcon />,
    accent: "#ff7043",
    features: ["Base Builder", "Base Defender", "Base Killer",
       "Tool - autolock, cloak hotkey",
       "Ship Killer"].sort(),
    specialRequirement: {
      text: "Requires additional license",
      highlight: true,
    },
  },
]

const Features = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
  const isTablet = useMediaQuery(theme.breakpoints.down("md"))

  // Image viewer states
  const [fullscreenImage, setFullscreenImage] = useState(null)
  const [activeTab, setActiveTab] = useState(0)

  // Image carousel states
  const mainImages = config.BOT_MAIN_IMAGES?.map((name) => `/bot-images/${name}`)

  const configImages = config.BOT_CONFIG_IMAGES?.map((name) => `/bot-images/config/${name}`) 

  const [mainIndex, setMainIndex] = useState(0)
  const [configIndex, setConfigIndex] = useState(0)

  // Navigation handlers
  const handleExternalNavigation = (url) => {
    window.open(url, "_blank", "noopener,noreferrer")
  }

  // Image carousel handlers
  const nextMain = () => setMainIndex((prev) => (prev + 1) % mainImages.length)
  const prevMain = () => setMainIndex((prev) => (prev - 1 + mainImages.length) % mainImages.length)

  const nextConfig = () => setConfigIndex((prev) => (prev + 1) % configImages.length)
  const prevConfig = () => setConfigIndex((prev) => (prev - 1 + configImages.length) % configImages.length)

  // Swipe gesture support (touch + mouse drag)
  const SWIPE_THRESHOLD = 50
  const swipeStart = useRef(null)

  const handleSwipeStart = (clientX, clientY = 0) => {
    swipeStart.current = { x: clientX, y: clientY }
  }

  const handleSwipeEnd = (clientX, onNext, onPrev) => {
    if (!swipeStart.current) return
    const delta = swipeStart.current.x - clientX
    swipeStart.current = null
    if (Math.abs(delta) < SWIPE_THRESHOLD) return
    if (delta > 0) onNext()
    else onPrev()
  }

  // Fullscreen swipe: horizontal navigates, vertical closes
  const handleFullscreenSwipeEnd = (clientX, clientY, onNext, onPrev) => {
    if (!swipeStart.current) return
    const deltaX = swipeStart.current.x - clientX
    const deltaY = swipeStart.current.y - clientY
    swipeStart.current = null
    if (Math.abs(deltaX) < SWIPE_THRESHOLD && Math.abs(deltaY) < SWIPE_THRESHOLD) return
    if (Math.abs(deltaY) > Math.abs(deltaX)) {
      closeFullscreen()
    } else if (deltaX > 0) {
      onNext()
    } else {
      onPrev()
    }
  }

  // Tab change handler
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
  }

  // Open fullscreen image
  const openFullscreen = (image) => {
    setFullscreenImage(image)
  }

  // Close fullscreen image
  const closeFullscreen = () => {
    setFullscreenImage(null)
  }

  // Active gallery context (used by carousel + fullscreen viewer)
  const activeImages = activeTab === 0 ? mainImages : configImages
  const activeIndex = activeTab === 0 ? mainIndex : configIndex
  const activeNext = activeTab === 0 ? nextMain : nextConfig
  const activePrev = activeTab === 0 ? prevMain : prevConfig

  return (
    <Container maxWidth={false} sx={{ py: 6, position: "relative", maxWidth: 3000, mx: "auto" }}>
      {/* Add the fan display of images in the background */}
      <Box
        sx={{
          position: "relative",
          minHeight: "100vh",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "flex-start",
          gap: 4,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            background: "rgba(13, 15, 25, 0.7)",
            border: "1px solid rgba(0, 229, 255, 0.1)",
            borderRadius: 2,
            position: "relative",
            overflow: "hidden",
            boxShadow: "0 0 20px rgba(0, 0, 0, 0.5)",
            zIndex: 2,
            flex: 1,
            minWidth: 0,
            width: "100%",
          }}
        >
          {/* Decorative elements */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              opacity: 0.03,
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='smallGrid' width='10' height='10' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 10 0 L 0 0 0 10' fill='none' stroke='%2300e5ff' strokeWidth='0.5'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23smallGrid)'/%3E%3C/svg%3E\")",
              zIndex: 0,
            }}
          />

          <Box sx={{ position: "relative", zIndex: 1 }}>

            {/* Bot Modes */}
            <Box
              sx={{
                mb: 4,
                p: 3,
                borderRadius: 2,
                background: "rgba(0, 0, 0, 0.3)",
                border: "1px solid rgba(0, 229, 255, 0.2)",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  color: "white",
                  mb: 3,
                  textAlign: "center",
                }}
              >
                Choose between two bot modes
              </Typography>

              <Grid container spacing={3}>
                {/* Memory Bot */}
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      background: "rgba(156, 39, 176, 0.06)",
                      border: "1px solid rgba(156, 39, 176, 0.25)",
                      height: "100%",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1.5, gap: 1.5 }}>
                      <MemoryIcon sx={{ fontSize: 28, color: "#ce93d8" }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "#ce93d8" }}>
                        Memory Bot
                      </Typography>
                    </Box>
                    <Typography variant="subtitle2" sx={{ color: "#ce93d8", fontWeight: "bold", mb: 1 }}>
                      Why use?
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {["See ship botting", "Game client visible", "Interact with ship while botting"].map((tag) => (
                        <Box
                          key={tag}
                          sx={{
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            bgcolor: "rgba(156, 39, 176, 0.1)",
                            border: "1px solid rgba(156, 39, 176, 0.2)",
                          }}
                        >
                          <Typography variant="caption" sx={{ color: "#ce93d8" }}>
                            {tag}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Grid>

                {/* Packet Bot */}
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      background: "rgba(0, 229, 255, 0.04)",
                      border: "1px solid rgba(0, 229, 255, 0.2)",
                      height: "100%",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1.5, gap: 1.5 }}>
                      <WifiTetheringIcon sx={{ fontSize: 28, color: theme.palette.primary.main }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: theme.palette.primary.main }}>
                        Packet Bot
                      </Typography>
                    </Box>
                    <Typography variant="subtitle2" sx={{ color: theme.palette.primary.main, fontWeight: "bold", mb: 1 }}>
                      Why use?
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {["Maximum performance - no game client", "Very low RAM & CPU usage"].map((tag) => (
                        <Box
                          key={tag}
                          sx={{
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            bgcolor: "rgba(0, 229, 255, 0.08)",
                            border: "1px solid rgba(0, 229, 255, 0.2)",
                          }}
                        >
                          <Typography variant="caption" sx={{ color: theme.palette.primary.main }}>
                            {tag}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              {/* Cross-mode notes */}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 3 }}>
                <Box
                  sx={{
                    flex: { xs: "1 1 100%", sm: "1 1 0" },
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: 2,
                    background: "rgba(0, 229, 255, 0.04)",
                    border: "1px solid rgba(0, 229, 255, 0.2)",
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#171a21",
                      flexShrink: 0,
                      fontSize: 44,
                      width: 44,
                      height: 44,
                      lineHeight: 0,
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "76%",
                        height: "76%",
                        borderRadius: "50%",
                        backgroundColor: "#ffffff",
                        zIndex: 0,
                      },
                    }}
                  >
                    <Box sx={{ position: "relative", zIndex: 1, display: "flex" }}>
                      <FaSteam />
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.85)" }}>
                    Works on Steam too — both Packet Bot and the game client (Memory Bot) are supported
                  </Typography>
                </Box>
                <Box
                  sx={{
                    flex: { xs: "1 1 100%", sm: "1 1 0" },
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: 2,
                    background: "rgba(0, 229, 255, 0.04)",
                    border: "1px solid rgba(0, 229, 255, 0.2)",
                  }}
                >
                  <CheckCircleOutlineIcon sx={{ color: theme.palette.primary.main, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.85)" }}>
                    Proxy support works for login
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Feature Categories with centered single items */}
            <Grid container spacing={3} justifyContent="center">
              {featureCategories.map((category, idx) => {
                const accent = category.accent || theme.palette.primary.main
                return (
                <Grid item xs={12} sm={6} xl={4} key={idx}>
                  <Card
                    sx={{
                      height: "100%",
                      background: category.specialRequirement?.highlight
                        ? `linear-gradient(135deg, rgba(0, 0, 0, 0.3) 0%, ${alpha(accent, 0.12)} 100%)`
                        : "rgba(0, 0, 0, 0.3)",
                      border: category.specialRequirement?.highlight
                        ? `1px solid ${alpha(accent, 0.4)}`
                        : "1px solid rgba(0, 229, 255, 0.2)",
                      borderRadius: 2,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        boxShadow: `0 0 15px ${alpha(accent, 0.3)}`,
                        transform: "translateY(-5px)",
                      },
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1.5 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            background: `linear-gradient(45deg, ${alpha(accent, 0.2)} 0%, ${alpha(
                              accent,
                              0.1,
                            )} 100%)`,
                            color: accent,
                          }}
                        >
                          {category.icon}
                        </Box>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: "bold",
                            color: accent,
                          }}
                        >
                          {category.name}
                        </Typography>
                      </Box>

                      <Box component="ul" sx={{ pl: 2, mb: category.specialRequirement ? 2 : 0 }}>
                        {category.features.map((feature, fidx) => (
                          <Box
                            component="li"
                            key={fidx}
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 1,
                              mb: 1.5,
                              listStyleType: "none",
                            }}
                          >
                            <CheckCircleOutlineIcon
                              sx={{
                                color: accent,
                                fontSize: "1.2rem",
                                mt: 0.2,
                                flexShrink: 0,
                              }}
                            />
                            <Typography variant="body2">{feature}</Typography>
                          </Box>
                        ))}
                      </Box>

                      {category.specialRequirement && (
                        <Box
                          sx={{
                            mt: 2,
                            p: 1.5,
                            borderRadius: 1,
                            bgcolor: category.specialRequirement.highlight
                              ? alpha(accent, 0.1)
                              : "rgba(255, 152, 0, 0.1)",
                            border: category.specialRequirement.highlight
                              ? `1px solid ${alpha(accent, 0.3)}`
                              : "1px solid rgba(255, 152, 0, 0.3)",
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: "medium",
                              color: category.specialRequirement.highlight
                                ? accent
                                : theme.palette.warning.main,
                            }}
                          >
                            {category.specialRequirement.text}
                          </Typography>

                          {category.specialRequirement.link && (
                            <Button
                              component={RouterLink}
                              to={category.specialRequirement.link}
                              size="small"
                              variant="outlined"
                              sx={{
                                alignSelf: "flex-start",
                                borderColor: theme.palette.warning.main,
                                color: theme.palette.warning.main,
                                "&:hover": {
                                  borderColor: theme.palette.warning.main,
                                  backgroundColor: "rgba(255, 152, 0, 0.1)",
                                },
                              }}
                            >
                              {category.specialRequirement.linkText}
                            </Button>
                          )}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
                )
              })}
            </Grid>

          </Box>
        </Paper>

        {/* Image Showcase Section */}
        <Paper
          elevation={3}
          sx={{
            scrollMarginTop: "80px",
            p: 4,
            background: "rgba(13, 15, 25, 0.7)",
            border: "1px solid rgba(0, 229, 255, 0.1)",
            borderRadius: 2,
            position: "relative",
            overflow: "hidden",
            boxShadow: "0 0 20px rgba(0, 0, 0, 0.5)",
            zIndex: 2,
            flex: { xs: "1 1 auto", md: "0 0 460px" },
            width: { xs: "100%", md: 460 },
            minWidth: 0,
            order: { xs: -1, md: -1 },
            position: { md: "sticky" },
            top: { md: 88 },
            alignSelf: { md: "flex-start" },
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            centered
            sx={{
              mb: 3,
              "& .MuiTabs-indicator": {
                backgroundColor: theme.palette.primary.main,
                height: 3,
                borderRadius: "3px",
                boxShadow: "0 0 8px rgba(0, 229, 255, 0.7)",
              },
              "& .MuiTab-root": {
                color: "rgba(255, 255, 255, 0.7)",
                "&.Mui-selected": {
                  color: theme.palette.primary.main,
                  fontWeight: "bold",
                },
              },
            }}
          >
            <Tab label="Main Interface" />
            <Tab label="Configuration" />
          </Tabs>

          {/* Main Images Tab */}
          {activeTab === 0 && (
            <Fade in={activeTab === 0} timeout={500}>
              <Box>
                <Box
                  onTouchStart={(e) => handleSwipeStart(e.changedTouches[0].clientX)}
                  onTouchEnd={(e) => handleSwipeEnd(e.changedTouches[0].clientX, nextMain, prevMain)}
                  onMouseDown={(e) => handleSwipeStart(e.clientX)}
                  onMouseUp={(e) => handleSwipeEnd(e.clientX, nextMain, prevMain)}
                  sx={{
                    position: "relative",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    mb: 2,
                    borderRadius: 2,
                    overflow: "hidden",
                    background: "rgba(0, 0, 0, 0.5)",
                    border: "1px solid rgba(0, 229, 255, 0.2)",
                    height: { xs: "250px", sm: "350px", md: "450px" },
                    touchAction: "pan-y",
                    userSelect: "none",
                  }}
                >
                  <IconButton
                    onClick={prevMain}
                    sx={{
                      position: "absolute",
                      left: 10,
                      zIndex: 2,
                      color: "white",
                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                      "&:hover": {
                        backgroundColor: "rgba(0, 229, 255, 0.3)",
                      },
                    }}
                  >
                    <ArrowBackIosIcon />
                  </IconButton>

                  <Box
                    component="img"
                    src={mainImages[mainIndex]}
                    alt={`Main Interface ${mainIndex + 1}`}
                    sx={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                    }}
                    onClick={() => openFullscreen(mainImages[mainIndex])}
                  />

                  <IconButton
                    onClick={nextMain}
                    sx={{
                      position: "absolute",
                      right: 10,
                      zIndex: 2,
                      color: "white",
                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                      "&:hover": {
                        backgroundColor: "rgba(0, 229, 255, 0.3)",
                      },
                    }}
                  >
                    <ArrowForwardIosIcon />
                  </IconButton>

                  <Tooltip title="View Fullscreen">
                    <IconButton
                      onClick={() => openFullscreen(mainImages[mainIndex])}
                      sx={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        zIndex: 2,
                        color: "white",
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        "&:hover": {
                          backgroundColor: "rgba(0, 229, 255, 0.3)",
                        },
                      }}
                    >
                      <FullscreenIcon />
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* Thumbnails */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  {mainImages.map((img, idx) => (
                    <Box
                      key={idx}
                      component="img"
                      src={img}
                      alt={`Thumbnail ${idx + 1}`}
                      onClick={() => setMainIndex(idx)}
                      sx={{
                        width: { xs: "60px", sm: "80px", md: "100px" },
                        height: { xs: "40px", sm: "60px", md: "75px" },
                        objectFit: "cover",
                        borderRadius: 1,
                        cursor: "pointer",
                        opacity: mainIndex === idx ? 1 : 0.6,
                        border: mainIndex === idx ? `2px solid ${theme.palette.primary.main}` : "2px solid transparent",
                        boxShadow: mainIndex === idx ? `0 0 8px ${theme.palette.primary.main}` : "none",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          opacity: 1,
                          transform: "scale(1.05)",
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Fade>
          )}

          {/* Config Images Tab */}
          {activeTab === 1 && (
            <Fade in={activeTab === 1} timeout={500}>
              <Box>
                <Box
                  onTouchStart={(e) => handleSwipeStart(e.changedTouches[0].clientX)}
                  onTouchEnd={(e) => handleSwipeEnd(e.changedTouches[0].clientX, nextConfig, prevConfig)}
                  onMouseDown={(e) => handleSwipeStart(e.clientX)}
                  onMouseUp={(e) => handleSwipeEnd(e.clientX, nextConfig, prevConfig)}
                  sx={{
                    position: "relative",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    mb: 2,
                    borderRadius: 2,
                    overflow: "hidden",
                    background: "rgba(0, 0, 0, 0.5)",
                    border: "1px solid rgba(0, 229, 255, 0.2)",
                    height: { xs: "250px", sm: "350px", md: "450px" },
                    touchAction: "pan-y",
                    userSelect: "none",
                  }}
                >
                  <IconButton
                    onClick={prevConfig}
                    sx={{
                      position: "absolute",
                      left: 10,
                      zIndex: 2,
                      color: "white",
                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                      "&:hover": {
                        backgroundColor: "rgba(0, 229, 255, 0.3)",
                      },
                    }}
                  >
                    <ArrowBackIosIcon />
                  </IconButton>

                  <Box
                    component="img"
                    src={configImages[configIndex]}
                    alt={`Configuration ${configIndex + 1}`}
                    sx={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                    }}
                    onClick={() => openFullscreen(configImages[configIndex])}
                  />

                  <IconButton
                    onClick={nextConfig}
                    sx={{
                      position: "absolute",
                      right: 10,
                      zIndex: 2,
                      color: "white",
                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                      "&:hover": {
                        backgroundColor: "rgba(0, 229, 255, 0.3)",
                      },
                    }}
                  >
                    <ArrowForwardIosIcon />
                  </IconButton>

                  <Tooltip title="View Fullscreen">
                    <IconButton
                      onClick={() => openFullscreen(configImages[configIndex])}
                      sx={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        zIndex: 2,
                        color: "white",
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        "&:hover": {
                          backgroundColor: "rgba(0, 229, 255, 0.3)",
                        },
                      }}
                    >
                      <FullscreenIcon />
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* Thumbnails */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  {configImages.map((img, idx) => (
                    <Box
                      key={idx}
                      component="img"
                      src={img}
                      alt={`Thumbnail ${idx + 1}`}
                      onClick={() => setConfigIndex(idx)}
                      sx={{
                        width: { xs: "60px", sm: "80px", md: "100px" },
                        height: { xs: "40px", sm: "60px", md: "75px" },
                        objectFit: "cover",
                        borderRadius: 1,
                        cursor: "pointer",
                        opacity: configIndex === idx ? 1 : 0.6,
                        border:
                          configIndex === idx ? `2px solid ${theme.palette.primary.main}` : "2px solid transparent",
                        boxShadow: configIndex === idx ? `0 0 8px ${theme.palette.primary.main}` : "none",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          opacity: 1,
                          transform: "scale(1.05)",
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Fade>
          )}
        </Paper>
      </Box>

      {/* Fullscreen Image Viewer */}
      {fullscreenImage && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            zIndex: 9999,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 2,
          }}
          onClick={closeFullscreen}
        >
          <IconButton
            onClick={closeFullscreen}
            sx={{
              position: "absolute",
              top: 20,
              right: 20,
              color: "white",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.2)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>

          <IconButton
            onClick={(e) => {
              e.stopPropagation()
              activePrev()
            }}
            sx={{
              position: "absolute",
              left: { xs: 8, sm: 24 },
              zIndex: 2,
              color: "white",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              "&:hover": {
                backgroundColor: "rgba(0, 229, 255, 0.3)",
              },
            }}
          >
            <ArrowBackIosIcon />
          </IconButton>

          <IconButton
            onClick={(e) => {
              e.stopPropagation()
              activeNext()
            }}
            sx={{
              position: "absolute",
              right: { xs: 8, sm: 24 },
              zIndex: 2,
              color: "white",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              "&:hover": {
                backgroundColor: "rgba(0, 229, 255, 0.3)",
              },
            }}
          >
            <ArrowForwardIosIcon />
          </IconButton>

          <Box
            onTouchStart={(e) => handleSwipeStart(e.changedTouches[0].clientX, e.changedTouches[0].clientY)}
            onTouchEnd={(e) =>
              handleFullscreenSwipeEnd(e.changedTouches[0].clientX, e.changedTouches[0].clientY, activeNext, activePrev)
            }
            onMouseDown={(e) => handleSwipeStart(e.clientX, e.clientY)}
            onMouseUp={(e) => handleFullscreenSwipeEnd(e.clientX, e.clientY, activeNext, activePrev)}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
              touchAction: "none",
              userSelect: "none",
            }}
          >
            <Box
              component="img"
              src={activeImages[activeIndex]}
              alt="Fullscreen view"
              draggable={false}
              onClick={(e) => e.stopPropagation()}
              sx={{
                maxWidth: "90%",
                maxHeight: "90%",
                objectFit: "contain",
                boxShadow: "0 0 30px rgba(0, 0, 0, 0.5)",
              }}
            />
          </Box>
        </Box>
      )}
    </Container>
  )
}

export default Features
